import { useMemo } from 'react';

/**
 * Hook optimisé pour calculer les statistiques des notes d'examen
 * Utilise des techniques de mémorisation pour améliorer les performances
 */
export const useExamenStats = (allNotes) => {
  return useMemo(() => {
    console.time('⏱️ Calcul des statistiques d\'examen');
    
    if (!allNotes || allNotes.length === 0) {
      console.timeEnd('⏱️ Calcul des statistiques d\'examen');
      return null;
    }

    const totalEtudiants = allNotes.length;
    
    // Pré-filtrage des données pour éviter les recalculs
    const notesAvecStatut = allNotes.map(note => ({
      ...note,
      noteValue: parseFloat(note.note),
      isAbsent: Boolean(note.absent),
      isTricheur: Boolean(note.triche),
      isMasculin: note.etudiant.genre === 'M' || note.etudiant.genre === 'MASCULIN',
      isFeminin: note.etudiant.genre === 'F' || note.etudiant.genre === 'FEMININ',
      hasValidNote: note.note !== null && note.note !== undefined && !note.absent && !note.triche
    }));

    // Calculs de base optimisés
    const absents = notesAvecStatut.filter(note => note.isAbsent).length;
    const tricheurs = notesAvecStatut.filter(note => note.isTricheur).length;
    const copiesNonRendues = notesAvecStatut.filter(note => !note.copie_rendue && !note.isAbsent).length;
    
    const notesValides = notesAvecStatut
      .filter(note => note.hasValidNote)
      .map(note => note.noteValue);

    const notesSaisies = notesValides.length;
    const pourcentageCompletion = Math.round((notesSaisies / totalEtudiants) * 100);

    if (notesSaisies === 0) {
      console.timeEnd('⏱️ Calcul des statistiques d\'examen');
      return {
        totalEtudiants,
        notesSaisies: 0,
        pourcentageCompletion,
        absents,
        tricheurs,
        copiesNonRendues,
        moyenne: 0,
        noteMin: 0,
        noteMax: 0,
        admis: 0,
        tauxReussite: 0,
        repartitionMentions: {},
        repartitionNotes: {},
        statsParGenre: {
          masculin: { total: 0, moyenne: 0, tauxReussite: 0, absents: 0, admis: 0 },
          feminin: { total: 0, moyenne: 0, tauxReussite: 0, absents: 0, admis: 0 }
        },
        chartData: { mentions: [], tranches: [], distribution: [], genre: [], repartitionGenre: [] }
      };
    }

    // Calculs statistiques de base
    const moyenne = notesValides.reduce((sum, note) => sum + note, 0) / notesValides.length;
    const noteMin = Math.min(...notesValides);
    const noteMax = Math.max(...notesValides);
    const admis = notesValides.filter(note => note >= 10).length;
    const tauxReussite = Math.round((admis / notesValides.length) * 100);

    // Statistiques par genre optimisées
    const notesMasculines = notesAvecStatut.filter(note => note.isMasculin);
    const notesFeminines = notesAvecStatut.filter(note => note.isFeminin);
    
    const notesValidesMasculines = notesMasculines.filter(note => note.hasValidNote).map(note => note.noteValue);
    const notesValidesFeminines = notesFeminines.filter(note => note.hasValidNote).map(note => note.noteValue);

    const moyenneMasculin = notesValidesMasculines.length > 0 
      ? Math.round((notesValidesMasculines.reduce((sum, note) => sum + note, 0) / notesValidesMasculines.length) * 100) / 100
      : 0;
    
    const moyenneFeminin = notesValidesFeminines.length > 0 
      ? Math.round((notesValidesFeminines.reduce((sum, note) => sum + note, 0) / notesValidesFeminines.length) * 100) / 100
      : 0;

    const tauxReussiteMasculin = notesValidesMasculines.length > 0 
      ? Math.round((notesValidesMasculines.filter(note => note >= 10).length / notesValidesMasculines.length) * 100)
      : 0;

    const tauxReussiteFeminin = notesValidesFeminines.length > 0 
      ? Math.round((notesValidesFeminines.filter(note => note >= 10).length / notesValidesFeminines.length) * 100)
      : 0;

    // Répartition des mentions optimisée
    const mentionCounts = allNotes.reduce((acc, note) => {
      if (note.mention) {
        acc[note.mention] = (acc[note.mention] || 0) + 1;
      }
      return acc;
    }, {});

    const repartitionMentions = {
      'EXCELLENT': mentionCounts.EXCELLENT || 0,
      'TRES_BIEN': mentionCounts.TRES_BIEN || 0,
      'BIEN': mentionCounts.BIEN || 0,
      'ASSEZ_BIEN': mentionCounts.ASSEZ_BIEN || 0,
      'PASSABLE': mentionCounts.PASSABLE || 0,
      'INSUFFISANT': mentionCounts.INSUFFISANT || 0
    };

    // Répartition des notes par tranches optimisée
    const repartitionNotes = notesValides.reduce((acc, note) => {
      if (note >= 16) acc['16-20']++;
      else if (note >= 14) acc['14-16']++;
      else if (note >= 12) acc['12-14']++;
      else if (note >= 10) acc['10-12']++;
      else if (note >= 8) acc['8-10']++;
      else acc['0-8']++;
      return acc;
    }, {
      '16-20': 0, '14-16': 0, '12-14': 0, 
      '10-12': 0, '8-10': 0, '0-8': 0
    });

    // Couleurs pour les graphiques
    const COLORS = {
      excellent: '#8B5CF6',
      tresBien: '#10B981',
      bien: '#3B82F6',
      assezBien: '#F59E0B',
      passable: '#F97316',
      insuffisant: '#EF4444'
    };

    // Données pour les graphiques optimisées
    const mentionsChartData = [
      { name: 'Excellent', value: repartitionMentions.EXCELLENT, range: '16-20', color: COLORS.excellent },
      { name: 'Très Bien', value: repartitionMentions.TRES_BIEN, range: '14-16', color: COLORS.tresBien },
      { name: 'Bien', value: repartitionMentions.BIEN, range: '12-14', color: COLORS.bien },
      { name: 'Assez Bien', value: repartitionMentions.ASSEZ_BIEN, range: '10-12', color: COLORS.assezBien },
      { name: 'Passable', value: repartitionMentions.PASSABLE, range: '8-10', color: COLORS.passable },
      { name: 'Insuffisant', value: repartitionMentions.INSUFFISANT, range: '0-8', color: COLORS.insuffisant }
    ].filter(item => item.value > 0);

    const tranchesChartData = Object.entries(repartitionNotes).map(([tranche, nombre]) => ({
      tranche,
      nombre,
      pourcentage: Math.round((nombre / notesSaisies) * 100)
    }));

    // Distribution des notes optimisée
    const distributionData = [];
    for (let i = 0; i <= 20; i += 2) {
      const count = notesValides.filter(note => note >= i && note < i + 2).length;
      distributionData.push({
        range: `${i}-${i + 2}`,
        count,
        pourcentage: Math.round((count / notesSaisies) * 100)
      });
    }

    // Données par genre
    const genreChartData = [
      {
        genre: 'Masculin',
        total: notesMasculines.length,
        notesSaisies: notesValidesMasculines.length,
        moyenne: moyenneMasculin,
        tauxReussite: tauxReussiteMasculin,
        absents: notesMasculines.filter(note => note.isAbsent).length,
        admis: notesValidesMasculines.filter(note => note >= 10).length
      },
      {
        genre: 'Féminin',
        total: notesFeminines.length,
        notesSaisies: notesValidesFeminines.length,
        moyenne: moyenneFeminin,
        tauxReussite: tauxReussiteFeminin,
        absents: notesFeminines.filter(note => note.isAbsent).length,
        admis: notesValidesFeminines.filter(note => note >= 10).length
      }
    ];

    const repartitionGenreData = [
      { name: 'Masculin', value: notesMasculines.length, color: '#3B82F6' },
      { name: 'Féminin', value: notesFeminines.length, color: '#EC4899' }
    ].filter(item => item.value > 0);

    const result = {
      totalEtudiants,
      notesSaisies,
      pourcentageCompletion,
      absents,
      tricheurs,
      copiesNonRendues,
      moyenne: Math.round(moyenne * 100) / 100,
      noteMin,
      noteMax,
      admis,
      tauxReussite,
      repartitionMentions,
      repartitionNotes,
      statsParGenre: {
        masculin: { 
          total: notesMasculines.length,
          notesValides: notesValidesMasculines,
          moyenne: moyenneMasculin, 
          tauxReussite: tauxReussiteMasculin,
          absents: notesMasculines.filter(note => note.isAbsent).length,
          tricheurs: notesMasculines.filter(note => note.isTricheur).length
        },
        feminin: { 
          total: notesFeminines.length,
          notesValides: notesValidesFeminines,
          moyenne: moyenneFeminin, 
          tauxReussite: tauxReussiteFeminin,
          absents: notesFeminines.filter(note => note.isAbsent).length,
          tricheurs: notesFeminines.filter(note => note.isTricheur).length
        }
      },
      chartData: {
        mentions: mentionsChartData,
        tranches: tranchesChartData,
        distribution: distributionData,
        genre: genreChartData,
        repartitionGenre: repartitionGenreData
      }
    };

    console.timeEnd('⏱️ Calcul des statistiques d\'examen');
    return result;
  }, [allNotes]);
}; 